import { InfoOutlineIcon, QuestionOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ChangeEvent, ClipboardEvent, useRef, useState } from 'react';
import { getLookupCoupon } from '../pages/api';
import { CouponLookupModel } from '../pages/api/type';
import { ResStatus } from '../lib/api/enum';
import { convertDateYYYYMMDDSymbol } from '../lib/util/regular';

interface CouponInputProps {
  couponList: CouponLookupModel[];
  setCouponList: (data: CouponLookupModel[]) => void;
}

const CouponInput = (props: CouponInputProps) => {
  const { couponList, setCouponList } = props;

  const toast = useToast();
  const oneRef = useRef<HTMLInputElement>(null);
  const twoRef = useRef<HTMLInputElement>(null);
  const threeRef = useRef<HTMLInputElement>(null);

  const isNumericAlphabet = (val: string) => {
    return val === '' || /^[A-Za-z0-9]+$/.test(val);
  };
  const [codeOne, setCodeOne] = useState<string>('');
  const onChangeCodeOne = (e: ChangeEvent<HTMLInputElement>) => {
    if (isNumericAlphabet(e.target.value)) {
      setCodeOne(e.target.value);
      if (e.target.value.length === 4 && twoRef.current !== null) {
        twoRef.current.focus();
      }
    }
  };
  const onHandlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.persist();
    const pasteValue = e.clipboardData.getData('text');
    if (pasteValue.length !== 12) return;
    // console.log(pasteValue.substring(0, 4));
    // console.log(pasteValue.substring(4, 8));
    // console.log(pasteValue.substring(8, 12));
    setCodeTwo(pasteValue.substring(4, 8));
    setCodeThree(pasteValue.substring(8, 12));
  };
  const [codeTwo, setCodeTwo] = useState<string>('');
  const onChangeCodeTwo = (e: ChangeEvent<HTMLInputElement>) => {
    if (isNumericAlphabet(e.target.value)) {
      setCodeTwo(e.target.value);
      if (e.target.value.length === 4 && threeRef.current !== null) {
        threeRef.current.focus();
      }
    }
  };
  const [codeThree, setCodeThree] = useState<string>('');
  const onChangeCodeThree = (e: ChangeEvent<HTMLInputElement>) => {
    if (isNumericAlphabet(e.target.value)) {
      setCodeThree(e.target.value);
    }
  };

  const basicMessage = "이용권 번호를 입력하고 '사용등록' 버튼을 누르세요";
  const [isError, setIsError] = useState<boolean>(false);
  const [msgInfo, setMsgInfo] = useState<string>(basicMessage);

  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const onSubmitCoupon = async () => {
    if (isCouponLoading) {
      toast({
        description: '신청을 처리 중입니다',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    // 기존 리스트 안에서 중복체크
    const codeInput = codeOne + codeTwo + codeThree;
    if (couponList.length > 0) {
      const finded = couponList.find(
        (param) => param.coupon_code === codeInput
      );
      if (finded !== undefined) {
        setIsError(true);
        setMsgInfo('등록된 쿠폰과 중복 사용이 불가능한 번호입니다');
        return;
      }
    }

    setIsCouponLoading(true);
    try {
      var codeRequestList = [];
      codeRequestList.push(codeInput);
      if (couponList.length > 0) {
        for (var i = 0; i < couponList.length; i++) {
          codeRequestList.push(couponList[i].coupon_code);
        }
      }
      const result = await getLookupCoupon({
        coupon_code: codeRequestList,
      });
      if (result.status === ResStatus.RES_SUCCESS) {
        setCouponList([...result.command.coupon_list]);
        setCodeOne('');
        setCodeTwo('');
        setCodeThree('');
        setIsError(false);
        setMsgInfo(basicMessage);
      } else {
        console.log(result.status + ': ' + result.result_message);
        setIsError(true);
        setMsgInfo(result.result_message);
      }
    } catch (err: any) {
      console.log(err);
      toast({
        title: '오류',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsCouponLoading(false);
    }
  };

  const onDeleteInList = (coupon_code: string) => {
    const remainList = couponList.filter(
      (param) => param.coupon_code !== coupon_code
    );
    setCouponList([...remainList]);
  };

  const onClickHelpCoupon = () => {
    window.open('https://mintapps.oopy.io/guide/coupon', '_blank');
  };

  return (
    <Box mt={'20px'} mb={'20px'} px={'20px'}>
      <HStack>
        <Heading fontSize={'16px'} fontWeight="semibold">
          이용권 번호
        </Heading>
        <QuestionOutlineIcon boxSize={'16px'} onClick={onClickHelpCoupon} />
      </HStack>
      <Flex mt={'16px'}>
        <HStack gap={0}>
          <Input
            padding={'10px'}
            ref={oneRef}
            w={'76px'}
            h={'54px'}
            fontSize={'15px'}
            fontWeight="bold"
            focusBorderColor={'black'}
            borderColor={'#eee'}
            borderRadius="base"
            textTransform={'uppercase'}
            value={codeOne}
            onPaste={onHandlePaste}
            onChange={onChangeCodeOne}
            maxLength={4}
          />
          <Center width={'15px'} height={'54px'}>
            -
          </Center>
          <Input
            padding={'10px'}
            ref={twoRef}
            w={'76px'}
            h={'54px'}
            fontSize={'15px'}
            fontWeight="bold"
            focusBorderColor={'black'}
            borderColor={'#eee'}
            borderRadius="base"
            textTransform={'uppercase'}
            value={codeTwo}
            onChange={onChangeCodeTwo}
            maxLength={4}
          />
          <Center width={'15px'} height={'54px'}>
            -
          </Center>
          <Input
            padding={'10px'}
            ref={threeRef}
            w={'76px'}
            h={'54px'}
            fontSize={'15px'}
            fontWeight="bold"
            focusBorderColor={'black'}
            borderColor={'#eee'}
            borderRadius="base"
            textTransform={'uppercase'}
            value={codeThree}
            onChange={onChangeCodeThree}
            maxLength={4}
          />
        </HStack>
        <Button
          h="54px"
          w="68px"
          ml={'9px'}
          fontSize={'14px'}
          fontWeight="semibold"
          borderRadius="base"
          colorScheme="black_button"
          isDisabled={
            codeOne.length < 4 || codeTwo.length < 4 || codeThree.length < 4
          }
          onClick={onSubmitCoupon}>
          사용등록
        </Button>
      </Flex>
      <HStack mt={'10px'} gap={'4px'}>
        <InfoOutlineIcon
          boxSize={'18px'}
          color={isError ? '#ff0000' : '#9e9e9e'}
        />
        <Text
          fontSize={'14px'}
          fontWeight="medium"
          color={isError ? '#ff0000' : '#9e9e9e'}>
          {msgInfo}
        </Text>
      </HStack>
      {couponList.length > 0 && (
        <Box mt={'20px'}>
          <Text fontSize={'13px'} fontWeight="semibold">
            등록된 이용권
          </Text>
          {couponList.map((menu, index) => (
            <HStack key={index} mt={'10px'} gap={0}>
              <VStack
                w={'259px'}
                bg={'#f3f4f6'}
                gap={'4px'}
                px={'15px'}
                py={'8px'}
                border={'1px'}
                borderRadius={'base'}
                borderColor={'#cfcfcf'}
                alignItems={'flex-start'}>
                <Text fontSize={'14px'} fontWeight="bold" color={'#191600'}>
                  {menu.coupon_name}
                </Text>
                <HStack w={'full'} justifyContent={'space-between'} gap={0}>
                  <Text fontSize={'12px'} fontWeight="medium" color={'#191600'}>
                    {menu.coupon_code.replace(/(.{4})/g, '$1-').slice(0, -1)}
                  </Text>
                  <Text fontSize={'12px'} fontWeight="medium" color={'#191600'}>
                    {`${menu.coupon_price} 원`}
                  </Text>
                </HStack>
                <Text fontSize={'9px'} fontWeight="medium" color={'#757575'}>
                  {`${convertDateYYYYMMDDSymbol(
                    menu.expire_date
                  )}까지 사용가능`}
                </Text>
              </VStack>
              <Center
                pl={'18px'}
                h={'50px'}
                cursor={'pointer'}
                onClick={() => onDeleteInList(menu.coupon_code)}>
                <Text
                  fontSize={'14px'}
                  fontWeight="semibold"
                  color={'red'}
                  as="ins">
                  등록해제
                </Text>
              </Center>
            </HStack>
          ))}
        </Box>
      )}
    </Box>
  );
};
export default CouponInput;
